import React, { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";

interface EditEquipmentSheetProps {
  equipmentId: Id<"equipment">;
  isOpen: boolean;
  onClose: () => void;
}

export function EditEquipmentSheet({ equipmentId, isOpen, onClose }: EditEquipmentSheetProps) {
  const [formData, setFormData] = useState({
    name: "",
    brand: "",
    model: "",
    serialNumber: "",
    purchaseDate: "",
    purchasePrice: "",
    warrantyExpirationDate: "",
    status: "In Service" as "In Service" | "Under Repair" | "Retired",
    locationId: "",
    assignmentNotes: "",
  });
  const { toast } = useToast();

  const equipment = useQuery(api.equipment.getById, { id: equipmentId });
  const updateEquipment = useMutation(api.equipment.update);
  const serviceLocations = useQuery(api.serviceLocations.getServiceLocations) || [];

  useEffect(() => {
    if (equipment) {
      setFormData({
        name: equipment.name,
        brand: equipment.brand,
        model: equipment.model,
        serialNumber: equipment.serialNumber || "",
        purchaseDate: equipment.purchaseDate,
        purchasePrice: equipment.purchasePrice?.toString() || "",
        warrantyExpirationDate: equipment.warrantyExpirationDate || "",
        status: equipment.status,
        locationId: equipment.location?.locationId || "",
        assignmentNotes: "", // We don't store assignment notes in equipment, would need to fetch from assignment
      });
    }
  }, [equipment]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.brand || !formData.model || !formData.purchaseDate) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateEquipment({
        id: equipmentId,
        name: formData.name,
        brand: formData.brand,
        model: formData.model,
        serialNumber: formData.serialNumber || undefined,
        purchaseDate: formData.purchaseDate,
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : undefined,
        warrantyExpirationDate: formData.warrantyExpirationDate || undefined,
        status: formData.status,
        locationId:
          formData.locationId && formData.locationId !== "none"
            ? (formData.locationId as any)
            : null,
        assignmentNotes: formData.assignmentNotes || undefined,
      });

      toast({
        title: "Success!",
        description: "Equipment updated successfully",
      });
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update equipment",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (!equipment) {
    return null;
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Edit Equipment</SheetTitle>
          <SheetDescription>Update the equipment information.</SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <form onSubmit={handleSubmit} className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="name">
                Equipment Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Enter equipment name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="brand">
                Brand <span className="text-red-500">*</span>
              </Label>
              <Input
                id="brand"
                value={formData.brand}
                onChange={(e) => handleInputChange("brand", e.target.value)}
                placeholder="Enter brand name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">
                Model <span className="text-red-500">*</span>
              </Label>
              <Input
                id="model"
                value={formData.model}
                onChange={(e) => handleInputChange("model", e.target.value)}
                placeholder="Enter model number"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="serialNumber">Serial Number</Label>
              <Input
                id="serialNumber"
                value={formData.serialNumber}
                onChange={(e) => handleInputChange("serialNumber", e.target.value)}
                placeholder="Enter serial number"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="purchaseDate">
                Purchase Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="purchaseDate"
                type="date"
                value={formData.purchaseDate}
                onChange={(e) => handleInputChange("purchaseDate", e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="purchasePrice">Purchase Price</Label>
              <Input
                id="purchasePrice"
                type="number"
                step="0.01"
                value={formData.purchasePrice}
                onChange={(e) => handleInputChange("purchasePrice", e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="warrantyExpirationDate">Warranty Expiration Date</Label>
              <Input
                id="warrantyExpirationDate"
                type="date"
                value={formData.warrantyExpirationDate}
                onChange={(e) => handleInputChange("warrantyExpirationDate", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">
                Status <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="In Service">In Service</SelectItem>
                  <SelectItem value="Under Repair">Under Repair</SelectItem>
                  <SelectItem value="Retired">Retired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="locationId">Assigned Location</Label>
              <Select
                value={formData.locationId || "none"}
                onValueChange={(value) =>
                  handleInputChange("locationId", value === "none" ? "" : value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a location (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Assignment</SelectItem>
                  {serviceLocations.map((location: any) => (
                    <SelectItem key={location._id} value={location._id}>
                      {location.name} ({location.businessName})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.locationId && formData.locationId !== "none" && (
              <div className="space-y-2">
                <Label htmlFor="assignmentNotes">Assignment Notes</Label>
                <Input
                  id="assignmentNotes"
                  value={formData.assignmentNotes}
                  onChange={(e) => handleInputChange("assignmentNotes", e.target.value)}
                  placeholder="Optional notes about this assignment"
                />
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1">
                Update Equipment
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
